<script setup lang="ts">
import { Folder, FolderTree } from 'lucide-vue-next'
import { ALLOWED_ACCEPT } from '~~/shared/allowedFileTypes'
import { storeToRefs } from 'pinia'
import type { ItemRow } from '~~/shared/items/itemRow'
import { useDocumentsStore } from '~/stores/documents'
import { useDocumentGroups } from '~/composables/useDocumentGroups'
import { useDocumentContextMenu, useOpenDocumentItem } from '~/composables/useDocumentContextMenu'
import { useCreateSheetFromSelection } from '~/composables/useCreateSheetFromSelection'
import { useExplorerKeyboard } from '~/composables/documents/useExplorerKeyboard'
import { useExplorerDrop } from '~/composables/documents/useExplorerDrop'
import { useExplorerCreateActions, type ExplorerCreateAction } from '~/composables/documents/useExplorerCreateActions'
import { filterDocumentGroups } from '~/utils/filterDocumentGroups'
import { folderListingRows, sidebarExplorerSurface, useRegisteredFileExplorer } from '~/composables/documents/fileExplorerSurface'
import { useUploadTargetStore } from '~/stores/uploadTarget'
import BrowserItemList from '~/components/documents/BrowserItemList.vue'
import BrowserGroupedList from '~/components/documents/BrowserGroupedList.vue'
import DeleteDocumentDialog from '~/components/documents/DeleteDocumentDialog.vue'
import DocumentContextMenu from '~/components/documents/DocumentContextMenu.vue'
import FileExplorerHeader from '~/components/documents/FileExplorerHeader.vue'
import FileExplorerToolbar from '~/components/documents/FileExplorerToolbar.vue'
import LoadFailureNotice from '~/components/documents/LoadFailureNotice.vue'
import RenameItemDialog from '~/components/documents/RenameItemDialog.vue'
import DismissibleError from '~/components/controls/DismissibleError.vue'

/**
 * The documents SIDEBAR. Its header, toolbar, keyboard map, drop targets and create menu are split
 * out (MAR-948); what stays here is the list, the delete flow and the search box's wiring.
 */
const { t } = useI18n()
const router = useRouter()
const uploadTarget = useUploadTargetStore()

const documents = useDocumentsStore()
const {
  currentPath, items, loading, selectedIds, searchQuery, dragItemId, activeSources, isFolderMode,
  flatVisibleItems, searchResults,
  // AlexAmin/marcus#469. `listFailed` displaces the rail's `common.noItems`; `searchFailed` is
  // #440's state, which the finder has rendered since that fix and this panel never did — the
  // sidebar's own search box went on saying `common.noResults` over a 500. Both arms of the one
  // ternary below were lying, so both are answered here.
  listFailed, searchFailed,
} = storeToRefs(documents)
// Groups are DERIVED from the cases/clients/documents stores, not fetched (MAR-125) — which is why a
// case created anywhere in the app appears here with no refetch.
const { groups: derivedGroups } = useDocumentGroups()
const {
  refreshAll, searchItems, clearSelection, createFolder, deleteItems, deleteSelected,
  setNavigationDomain, setGroupFilter, fetchItems,
} = documents

/**
 * The sidebar's own display filter over the store's groups (MAR-125).
 *
 * Filtering is this component's business, not the store's — the store owns WHICH documents exist
 * and the server-side ordering (AlexAmin/marcus#169: there is deliberately NO client-side sort here;
 * a comparator over a band's loaded page would fight the server's order and win, silently), and
 * every surface applies its own lens. The VALUE sits in the store, in a field of its own
 * (`groupFilter`, MAR-494) — the agent has to be told what the user is looking at — but `/app/docs`
 * binds to `searchQuery` and this to `groupFilter`, so neither surface can move the other's.
 */
const groupSearch = computed({
  get: () => documents.groupFilter,
  set: (v: string) => setGroupFilter(v),
})
const visibleGroups = computed(() => filterDocumentGroups(derivedGroups.value, groupSearch.value))

// Keyboard nav / select-all run over what is actually on screen, so in grouped mode the sidebar —
// which owns the filter — tells the store what that is. Includes nested matter documents, or a
// client-nested document would be invisible to select-all / arrow-key navigation.
watchEffect(() => {
  setNavigationDomain(
    isFolderMode.value
      ? null
      : visibleGroups.value.flatMap(g => [
          ...g.documents.map(d => d.id),
          ...(g.subgroups?.flatMap(s => s.documents.map(d => d.id)) ?? []),
        ]),
  )
})

// "New sheet" from the selection — documents only, failure shown inline (MAR-957). The same
// composable backs the finder's button, so the two cannot drift again.
const {
  documentIds: sheetDocumentIds,
  creating: creatingSheet,
  error: sheetError,
  create: createSheetFromSelection,
} = useCreateSheetFromSelection()

// ── New folder inline create ──
const creatingFolder = ref(false)
const newFolderName = ref('')
const newFolderInput = ref<HTMLInputElement | null>(null)

function startCreateFolder() {
  creatingFolder.value = true
  newFolderName.value = ''
  nextTick(() => newFolderInput.value?.focus())
}

async function submitCreateFolder() {
  const name = newFolderName.value.trim()
  if (!name) { creatingFolder.value = false; return }
  await createFolder(name)
  creatingFolder.value = false
  newFolderName.value = ''
}

// ── Rename ──
// The context menu's "Rename" and F2 both open `RenameItemDialog` (MAR-957).
const renameTarget = ref<{ id: string; name: string } | null>(null)

function startRename(item: ItemRow) {
  renameTarget.value = { id: item.id, name: item.name }
}

// ── Context menu ──
// ONE component with the finder (`DocumentContextMenu`, MAR-957). What stays here is only what this
// surface does differently: how a delete is confirmed, and where an action's failure is shown.
const { menu: contextMenu, open: openContextMenu, close: closeContextMenu } = useDocumentContextMenu()
const openItem = useOpenDocumentItem()
const actionError = ref('')

// ── Delete ──
const deleting = ref(false)
const deleteError = ref('')

/**
 * The single-item delete goes through `DeleteDocumentDialog` (MAR-174) — a `window.confirm` could
 * not say which expedientes reference the document, which is the one fact worth knowing before
 * deleting a legal file. A multi-select delete still confirms inline, in the header's strip: N
 * documents means N different reference sets, and a dialog that listed them all would be a wall.
 */
const deleteTarget = ref<{ id: string; name: string; type?: string } | null>(null)
const deleteDialog = ref<InstanceType<typeof DeleteDocumentDialog> | null>(null)

function askDeleteOne(item: ItemRow) {
  deleteTarget.value = { id: item.id, name: item.name, type: item.type }
  nextTick(() => deleteDialog.value?.load())
}

async function onDialogDeleted() {
  deleteTarget.value = null
  await refreshAll()
}

/**
 * The pending multi-delete (GitHub #50, draft 05). All three entry points — the header button, the
 * Delete key, the context menu — funnel through `confirmAndDelete`, which arms the header's strip,
 * so the keyboard path asks the same question as the mouse one.
 */
const header = ref<InstanceType<typeof FileExplorerHeader> | null>(null)
const pendingDelete = ref<{ ids: string[], useSelection: boolean } | null>(null)

const pendingDeleteQuestion = computed(() => {
  const ids = pendingDelete.value?.ids ?? []
  const rows = items.value.filter(i => ids.includes(i.id))
  return t(deleteConfirmKey(rows), ids.length)
})

async function confirmAndDelete(targets: ItemRow[] | 'selected') {
  if (deleting.value) return
  const ids = targets === 'selected' ? [...selectedIds.value] : targets.map(item => item.id)
  if (ids.length === 0) return
  if (targets !== 'selected' && targets.length === 1) return askDeleteOne(targets[0]!)
  pendingDelete.value = { ids, useSelection: targets === 'selected' }
  header.value?.arm()
}

async function runPendingDelete() {
  const pending = pendingDelete.value
  if (!pending) return
  deleting.value = true
  deleteError.value = ''
  try {
    if (pending.useSelection) await deleteSelected()
    else await deleteItems(pending.ids)
  } catch (err: any) {
    // Not an API error: `deleteItems` logs each request's failure and throws its own English
    // aggregate, so the count is the only thing worth showing.
    const count = err?.result?.failedIds?.length || pending.ids.length
    deleteError.value = count === 1 ? t('documents.deleteDialog.failed') : `${t('documents.deleteDialog.failed')} (${count})`
  } finally {
    deleting.value = false
    pendingDelete.value = null
  }
}

async function handleContextDelete(ids: string[], item: ItemRow) {
  const targets = ids.map(id => (id === item.id ? item : documents.get(id))).filter((r): r is ItemRow => !!r)
  // Fall back to the selection when some targets aren't resolvable (not cached).
  await confirmAndDelete(targets.length === ids.length ? targets : 'selected')
}

// ── Keyboard ──
// Root element of the panel — keyboard shortcuts only fire when focus is inside it, so Cmd+A /
// Delete don't hijack the whole app.
const panelRef = ref<HTMLElement | null>(null)
const { onKeyDown } = useExplorerKeyboard({
  panel: panelRef,
  selectedCount: () => selectedIds.value.size,
  focusedItem: documents.focusedItem,
  isExpanded: documents.isExpanded,
  moveSelection: documents.moveSelection,
  expandFolder: documents.expandFolder,
  collapseFolder: documents.collapseFolder,
  open: openItem,
  rename: startRename,
  deleteSelected: () => confirmAndDelete('selected'),
  toggleSelectAll: () => toggleSelectAll(),
})

// ── + New ──
const newMenuOpen = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const createActions = useExplorerCreateActions({
  t, isFolderMode, activeSources, startCreateFolder,
  pickFiles: () => fileInput.value?.click(),
  go: path => router.push(path),
})

function runCreateAction(action: ExplorerCreateAction) {
  newMenuOpen.value = false
  action.run()
}

/**
 * Ask where the document belongs before uploading it (#83) — one store owns that question for every
 * entry point, and it is what performs the upload once the answer exists. The picker opens
 * pre-selected from the route, so on a client or matter page this is a single confirming click.
 */
function handleFileUpload(e: Event) {
  const input = e.target as HTMLInputElement
  const list = input.files ? Array.from(input.files) : []
  input.value = ''
  newMenuOpen.value = false
  if (!list.length) return
  uploadTarget.requestUpload(list, { path: currentPath.value })
}

// ── Drag and drop ──
const {
  breadcrumbDropTarget, listDropActive,
  onInternalDragOver, onInternalDrop, onFilesDragOver, onFilesDrop, onContainerDragLeave,
} = useExplorerDrop({
  isFolderMode, dragItemId, currentPath, dropOnto: documents.dropOnto,
  requestUpload: (files, opts) => uploadTarget.requestUpload(files, opts),
})

// Click on empty list space (not a row) clears the selection, Finder-style.
function onListBackgroundClick(e: MouseEvent) {
  if (e.target === e.currentTarget) clearSelection()
}

// ── Search ──
// The box starts on whatever search is already running: the store field survives this component
// (the finder page sets it too), so opening the drawer after a search would otherwise show a
// filtered list above an empty input — the same "did that work?" the chat tool used to produce.
const searchInput = ref(isFolderMode.value ? searchQuery.value : groupSearch.value)
let searchTimer: ReturnType<typeof setTimeout> | null = null

/** Apply what is in the box. Folder mode hits the server search; grouped modes filter the loaded
 *  groups client-side. */
function runSearch() {
  if (isFolderMode.value) searchItems(searchInput.value)
  else groupSearch.value = searchInput.value
}

function onSearchInput() {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(runSearch, 300)
}

/**
 * `filter_files` (chat) — set the box and apply it, with no debounce: a programmatic call has no
 * keystrokes, and waiting 300ms would only mean the agent reports success before the list moves.
 */
function applyFilter(query: string) {
  if (searchTimer) clearTimeout(searchTimer)
  // The column lens is dropped first (AlexAmin/marcus#169). The agent reports back "showing your
  // leases"; if a type/state filter set on `/app/docs` survived that, the list underneath would be
  // the leases narrowed to Final and nothing in the sidebar — which has no column headers — would
  // say so. The tool's claim has to be true of what is on screen.
  documents.clearDocumentFilters()
  searchInput.value = query
  runSearch()
}

/**
 * What folder mode lists: the search hits while a search runs, the folder otherwise (MAR-989). It
 * rendered `items` whatever the box said, so in "Árbol de carpetas" a search — typed, or set by
 * `filter_files` — moved nothing on screen (CHAT-2.01). The same function tells the chat what is
 * listed, so the two cannot disagree.
 */
const listedItems = computed(() => folderListingRows({
  searchQuery: searchQuery.value, searchResults: searchResults.value, items: items.value,
}))

// This sidebar is a file explorer the chat can see and drive while it is mounted (MAR-989).
useRegisteredFileExplorer(() => sidebarExplorerSurface(documents, applyFilter))

function clearSearch() {
  searchInput.value = ''
  if (isFolderMode.value) searchItems('')
  else groupSearch.value = ''
}

// Changing the source selection resets THIS toolbar's search box; the store clears the shared group
// filter, and `DocumentSourceFilter` loads whatever entities the new selection groups by.
function onSourcesChanged() {
  searchInput.value = ''
}

// Select all — targets the folder listing, or the grouped documents when grouped.
const selectAllTargets = computed(() => (isFolderMode.value ? listedItems.value : flatVisibleItems.value))
const allSelected = computed(() =>
  selectAllTargets.value.length > 0 && selectAllTargets.value.every(i => selectedIds.value.has(i.id)))

function toggleSelectAll() {
  if (allSelected.value) clearSelection()
  else selectedIds.value = new Set(selectAllTargets.value.map(i => i.id))
}
</script>

<template>
  <div
    ref="panelRef"
    class="relative flex flex-col h-full bg-sidebar-panel outline-none"
    tabindex="-1"
    @keydown="onKeyDown"
  >
    <FileExplorerHeader
      ref="header"
      :delete-question="pendingDeleteQuestion"
      :delete-action="runPendingDelete"
      :can-create-sheet="sheetDocumentIds.length > 0"
      :creating-sheet="creatingSheet"
      :deleting="deleting"
      :drop-target="breadcrumbDropTarget"
      @cancel-delete="pendingDelete = null"
      @create-sheet="createSheetFromSelection"
      @delete-selected="confirmAndDelete('selected')"
      @crumb-dragover="(e, path) => onInternalDragOver(e, { breadcrumb: path })"
      @crumb-dragleave="(e) => onContainerDragLeave(e, () => breadcrumbDropTarget = null)"
      @crumb-drop="onInternalDrop"
    />

    <FileExplorerToolbar
      v-model:search="searchInput"
      v-model:new-menu-open="newMenuOpen"
      :create-actions="createActions"
      :keep-focus-on-close="creatingFolder"
      @input="onSearchInput"
      @clear="clearSearch"
      @run-action="runCreateAction"
      @sources-changed="onSourcesChanged"
    />
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="ALLOWED_ACCEPT"
      multiple
      @change="handleFileUpload"
    >

    <!-- A sheet that could not be created, or a context-menu action (move, download) that failed. -->
    <DismissibleError v-if="sheetError || actionError" :message="sheetError || actionError" @dismiss="sheetError = ''; actionError = ''" />
    <DismissibleError v-if="deleteError" :message="deleteError" @dismiss="deleteError = ''" />

    <!-- Inline create folder -->
    <div v-if="creatingFolder" class="px-3 py-2 border-b shrink-0">
      <div class="flex items-center gap-2">
        <Folder class="h-4 w-4 text-warning-ink shrink-0" />
        <input
          ref="newFolderInput"
          v-model="newFolderName"
          :placeholder="t('common.newFolder')"
          class="flex-1 h-7 px-2 text-xs rounded-md border border-input bg-transparent placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          @keyup.enter="submitCreateFolder"
          @keyup.escape="creatingFolder = false"
        />
        <Button size="sm" class="h-7 px-2 text-xs" :disabled="!newFolderName.trim()" @click="submitCreateFolder">
          {{ t('common.create') }}
        </Button>
      </div>
    </div>

    <!-- Item list -->
    <div
      class="flex-1 min-h-0 overflow-y-auto transition-colors"
      :class="listDropActive && 'bg-primary-lighter'"
      @click="onListBackgroundClick"
      @dragover="onInternalDragOver($event, { list: true }); onFilesDragOver($event)"
      @dragleave="onContainerDragLeave($event, () => listDropActive = false)"
      @drop="onInternalDrop($event, currentPath); onFilesDrop($event)"
    >
      <!-- Grouped view (by case / Client) -->
      <BrowserGroupedList
        v-if="!isFolderMode"
        :groups="visibleGroups"
        :filtered="groupSearch.trim().length > 0"
        @contextmenu="openContextMenu"
      />

      <!-- Loading -->
      <div v-else-if="loading && listedItems.length === 0" class="flex items-center justify-center py-12">
        <div class="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>

      <!--
        A LISTING OR A SEARCH THAT BROKE (AlexAmin/marcus#469), above the empty state that would
        otherwise speak for it. `fetchItems` swallowed its failure under a `// tracked by useApi`
        comment that is false for a 500, so the rail said `common.noItems` — "you have no
        documents" — beside a "Subir" button, over a request that never answered.

        Both arms, because both were wrong: the search half is #440's `searchFailed`, which the
        finder has rendered since that fix while this panel, which owns its own search box, went on
        saying `common.noResults`. Retrying re-issues whichever request is actually on screen.
      -->
      <LoadFailureNotice
        v-else-if="(searchInput ? searchFailed : listFailed) && listedItems.length === 0 && !creatingFolder"
        :message="searchInput ? t('documents.searchFailed') : t('documents.listFailed')"
        :hint="searchInput ? t('documents.searchFailedHint') : t('documents.listFailedHint')"
        @retry="searchInput ? searchItems(searchInput) : fetchItems()"
      />

      <!-- Empty state -->
      <div v-else-if="listedItems.length === 0 && !creatingFolder" class="px-4 py-12 text-center">
        <p class="text-sm text-muted-foreground">
          {{ searchInput ? t('common.noResults') : t('common.noItems') }}
        </p>
        <div v-if="!searchInput" class="mt-3 flex items-center justify-center gap-2">
          <Button
            v-for="action in createActions"
            :key="action.key"
            variant="outline"
            size="sm"
            @click="runCreateAction(action)"
          >
            <component :is="action.icon" class="h-3.5 w-3.5 mr-1.5" />
            {{ action.label }}
          </Button>
        </div>
      </div>

      <!-- Items -->
      <div v-else class="py-1">
        <BrowserItemList
          :items="listedItems"
          @contextmenu="openContextMenu"
        />
      </div>
    </div>

    <!-- Show all documents → full-page finder -->
    <div class="shrink-0 border-t p-2">
      <button
        type="button"
        class="w-full flex items-center justify-center gap-1.5 h-8 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent-lighter transition-colors"
        @click="router.push('/app/docs')"
      >
        <FolderTree class="h-3.5 w-3.5" />
        {{ t('documents.showAll') }}
      </button>
    </div>

    <!-- Context menu — the one the finder uses too (MAR-957). -->
    <DocumentContextMenu
      :menu="contextMenu"
      @close="closeContextMenu"
      @rename="startRename"
      @delete="handleContextDelete"
      @error="(message) => actionError = message"
    />

    <!-- Rename (context menu and F2). -->
    <RenameItemDialog :target="renameTarget" @close="renameTarget = null" />

    <!-- Single-item delete confirmation (MAR-174): names the expedientes that reference the file. -->
    <DeleteDocumentDialog
      ref="deleteDialog"
      :target="deleteTarget"
      @deleted="onDialogDeleted"
      @close="deleteTarget = null"
    />
  </div>
</template>
